import React, { useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSplash } from '../../context/SplashContext';

// Sign-in. Username and password, no second factor — the platform owner's decision for
// this deployment.
//
// The form NEVER reacts to the username on its own: both fields are collected and
// submitted together. Looking up a username before the password is checked would turn
// this page into an account-enumeration oracle. The server returns one identical refusal
// for a wrong password, an unknown user and a user with no role; this page shows exactly
// that and adds nothing.
export default function LoginPage() {
  const { login, isAuthenticated, loading } = useAuth();
  const { playSplash } = useSplash();
  const nav = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const enteringRef = useRef(false);

  if (loading) return null;
  // The guard below would otherwise cut the entry animation off before it began: a
  // successful login flips `isAuthenticated`, this component re-renders, and <Navigate>
  // lands on the portal at once — the splash is meant to CARRY that navigation, not follow
  // it. A ref, not state: it is read during the very render that the auth flip triggers,
  // and a setState would not have been applied by then.
  if (isAuthenticated && !enteringRef.current) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    // Set BEFORE the await. login() flips the auth state, and the render that follows it
    // reads this ref — claiming the transition afterwards would already be too late.
    enteringRef.current = true;
    try {
      await login(username.trim(), password);
      // The signal field, as the way into the portal. playSplash fires its callback
      // mid-animation while the backdrop is still opaque, so the portal mounts underneath
      // and the crossfade reveals it rather than cutting to it.
      playSplash(() => nav('/', { replace: true }), 3800,
                 { tagline: 'Signals in · Intelligence out', cap: true });
    } catch (err) {
      enteringRef.current = false;
      setError(err?.response?.status === 429
        ? 'Too many attempts. Please wait a while and try again.'
        : (err?.response?.data?.error || 'Invalid credentials.'));
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f7f9fc] px-4 dark:bg-[#0b0d12]">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="font-serif text-2xl text-gray-900 dark:text-gray-100">
            Maghaweer<span className="text-brand-gold">.ai</span>
          </div>
          <div className="mt-1 text-[9px] uppercase tracking-[3px] text-gray-400 dark:text-gray-600">
            Investment governance
          </div>
        </div>

        <form onSubmit={submit}
              className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-[#0d1117]">
          <label className="block">
            <span className="text-[9px] uppercase tracking-[2px] text-gray-400 dark:text-gray-500">Username</span>
            <input type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                   autoComplete="username" autoFocus
                   className="mt-1.5 w-full rounded-md border border-gray-200 bg-transparent px-3 py-2 text-sm text-gray-900 outline-none focus:border-brand-gold dark:border-gray-700 dark:text-gray-100" />
          </label>

          <label className="mt-4 block">
            <span className="text-[9px] uppercase tracking-[2px] text-gray-400 dark:text-gray-500">Password</span>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                   autoComplete="current-password"
                   className="mt-1.5 w-full rounded-md border border-gray-200 bg-transparent px-3 py-2 text-sm text-gray-900 outline-none focus:border-brand-gold dark:border-gray-700 dark:text-gray-100" />
          </label>

          {error && (
            <div className="mt-4 rounded border-l-2 border-rose-500 bg-rose-50 px-3 py-2 text-[12px] text-rose-900 dark:bg-rose-500/10 dark:text-rose-200">
              {error}
            </div>
          )}

          <button type="submit" disabled={busy || !username || !password}
                  className="mt-5 w-full rounded-md bg-brand-gold py-2.5 text-sm font-medium text-white transition-opacity disabled:opacity-40">
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="mt-5 text-center text-[11px] leading-relaxed text-gray-400 dark:text-gray-600">
          Private and confidential.
        </p>
      </div>
    </div>
  );
}
